<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@/composables/api'
import { useUserExtendedData } from '@/composables/useUserExtendedData'
import { useAccess } from '@/composables/useAccess'
import type { components } from '@/types/_api'
import BDialog from '@/components/base/BDialog/BDialog.vue'
import FOnboardingCompleted from '@/components/feature/FOnboardingCompleted/FOnboardingCompleted.vue'

type Partner = components['schemas']['Partner']

const { t } = useI18n()
const { canAccessResource } = useAccess()
const route = useRoute()

const COMPLETED_STORAGE_KEY = 'creditor-onboarding-completed'

const dialogOpen = ref(false)

const userExtendedDataQuery = useUserExtendedData()
const partnerId = computed(() => userExtendedDataQuery.data.value?.partner ?? '')
const canAccessPartners = canAccessResource('partners')

const partnerQuery = useQuery(
  '/partners/{resource_id}',
  computed(() => ({
    path: { resource_id: partnerId.value },
  })),
  {
    enabled: computed(() => partnerId.value !== '' && canAccessPartners.value && dialogOpen.value),
  }
)

const partner = computed<Partner | null>(() => partnerQuery.data.value ?? null)

const customPartnerTexts = computed(() => {
  if (partner.value == null) return null
  const config = partner.value.creditor_onboarding_config
  const partnerNameText = config?.texts?.partner_name
  const dialogMessageText = config?.texts?.completed_dialog_message
  const urlButtonText = config?.texts?.completed_dialog_url_button
  return {
    partnerName: partnerNameText === '' || partnerNameText == null ? partner.value.name : partnerNameText,
    dialogMessage: dialogMessageText === '' ? null : dialogMessageText,
    urlButton: urlButtonText === '' ? null : urlButtonText,
  }
})

const hasCustomDialogMessage = computed(() => customPartnerTexts.value?.dialogMessage != null)

function checkAndOpenDialog() {
  const storedValue = sessionStorage.getItem(COMPLETED_STORAGE_KEY)
  if (storedValue === 'true' && !dialogOpen.value) {
    dialogOpen.value = true
  }
}

watch(() => route.fullPath, checkAndOpenDialog)

checkAndOpenDialog()

function closeDialog() {
  dialogOpen.value = false
  sessionStorage.setItem(COMPLETED_STORAGE_KEY, 'false')
}
</script>

<template>
  <b-dialog
    v-model="dialogOpen"
    size="large"
    persistent
    hide-footer
    hide-header
  >
    <div class="pa-6">
      <f-onboarding-completed>
        <template
          v-if="partner != null"
          #message
        >
          <p
            v-if="hasCustomDialogMessage"
            class="text-center"
          >
            {{ customPartnerTexts?.dialogMessage }}
          </p>

          <i18n-t
            v-else
            keypath="common.partner_creditor_onboarding_completed_message"
            tag="p"
            class="text-center"
          >
            <template #partner_name>
              <strong>{{ customPartnerTexts?.partnerName }}</strong>
            </template>
          </i18n-t>
        </template>

        <template #actions>
          <v-btn
            :variant="partner?.url ? 'outlined' : 'flat'"
            class="mr-4"
            color="primary"
            @click="closeDialog"
          >
            {{ t('common.explore_amili') }}
          </v-btn>

          <v-btn
            v-if="partner?.url"
            color="primary"
            :href="partner.url"
          >
            {{ customPartnerTexts?.urlButton ?? `${t('common.back_to')} ${customPartnerTexts?.partnerName ?? ''}` }}
          </v-btn>
        </template>
      </f-onboarding-completed>
    </div>
  </b-dialog>
</template>
